import React, { useState, useCallback } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { Check, ChevronLeft, ChevronRight, Loader2, CalendarCheck, Copy } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { useSettings } from "@/contexts/SettingsContext";
import api, { errMsg } from "@/lib/api";
import { CATEGORY_KEYS } from "@/i18n";
import { Calendar } from "@/components/ui/calendar";

const WEEKDAY_JS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const tomorrow = () => { const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + 1); return d; };

export const BookingWizard = ({ presetCategory }) => {
  const { t, lang } = useLang();
  const { img, settings } = useSettings();
  const [step, setStep] = useState(presetCategory ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [form, setForm] = useState({
    category: presetCategory || "", date: "", time: "", name: "", email: "", phone: "", guest_count: 1, notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const fetchSlots = useCallback(async (dateStr) => {
    setSlotsLoading(true);
    try {
      const { data } = await api.get("/availability", { params: { date: dateStr } });
      setSlots(data.slots || []);
    } catch (e) {
      setSlots([]);
    } finally {
      setSlotsLoading(false);
    }
  }, []);

  const onPickDate = (date) => {
    if (!date) return;
    const ds = format(date, "yyyy-MM-dd");
    setSelectedDate(date);
    set("date", ds);
    set("time", "");
    fetchSlots(ds);
  };

  const isClosedDay = (date) => {
    const h = settings?.opening_hours?.[WEEKDAY_JS[date.getDay()]];
    return h ? !!h.closed : false;
  };

  const canNext = () => {
    if (step === 0) return !!form.category;
    if (step === 1) return !!form.date && !!form.time;
    if (step === 2) return form.name.trim() && form.email.trim() && form.phone.trim();
    return true;
  };

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/appointments", { ...form, language: lang });
      setResult(data);
    } catch (e) {
      toast.error(errMsg(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setStep(presetCategory ? 1 : 0);
    setSelectedDate(null); setSlots([]);
    setForm({ category: presetCategory || "", date: "", time: "", name: "", email: "", phone: "", guest_count: 1, notes: "" });
  };

  if (result) {
    return (
      <div className="vl-card rounded-2xl p-8 sm:p-12 text-center vl-fade-up" data-testid="booking-success">
        <div className="mx-auto w-16 h-16 rounded-full border border-[var(--vl-gold)] flex items-center justify-center mb-6">
          <CalendarCheck className="text-[var(--vl-gold)]" size={30} />
        </div>
        <h3 className="font-serif-display text-3xl text-[var(--vl-ivory)]">{t("success_title")}</h3>
        <p className="text-sm text-[var(--vl-stone)] mt-4 max-w-md mx-auto leading-relaxed">{t("success_text")}</p>
        <button onClick={() => { navigator.clipboard?.writeText(result.ref); toast.success(result.ref); }}
          className="mt-6 inline-flex items-center gap-2 font-mono-accent text-2xl text-[var(--vl-gold)] tracking-widest" data-testid="booking-ref">
          {result.ref} <Copy size={16} />
        </button>
        <p className="text-xs text-[var(--vl-stone)]/70 mt-4 max-w-sm mx-auto">{t("success_hint")}</p>
        <button onClick={reset} className="vl-btn-outline rounded-full px-7 py-3 text-sm tracking-widest uppercase mt-8" data-testid="book-another-btn">
          {t("book_another")}
        </button>
      </div>
    );
  }

  const steps = [t("step1_title"), t("step2_title"), t("step3_title"), t("step4_title")];

  return (
    <div className="vl-card rounded-2xl p-6 sm:p-10" data-testid="booking-wizard">
      <div className="flex items-center gap-2 mb-8">
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium shrink-0 transition-colors ${i <= step ? "bg-[var(--vl-gold)] text-[var(--vl-obsidian)]" : "border border-[var(--vl-border)] text-[var(--vl-stone)]"}`}>
              {i < step ? <Check size={15} /> : i + 1}
            </div>
            {i < steps.length - 1 && <div className={`h-px flex-1 ${i < step ? "bg-[var(--vl-gold)]" : "bg-[var(--vl-border)]"}`} />}
          </React.Fragment>
        ))}
      </div>

      <div className="text-xs tracking-widest uppercase text-[var(--vl-gold)]/80 mb-1">{t("step")} {step + 1} {t("of")} 4</div>
      <h3 className="font-serif-display text-2xl sm:text-3xl text-[var(--vl-ivory)] mb-7">{steps[step]}</h3>

      {step === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="step-category">
          {CATEGORY_KEYS.map((cat) => (
            <button key={cat} onClick={() => set("category", cat)}
              className={`group relative overflow-hidden rounded-xl text-left h-28 border transition-all ${form.category === cat ? "border-[var(--vl-gold)]" : "border-[var(--vl-border)]"}`}
              data-testid={`category-select-${cat}`}>
              <img src={img(`cat_${cat}`)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-45 group-hover:opacity-60 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/30" />
              <div className="relative p-4 flex items-center justify-between h-full">
                <span className="font-serif-display text-xl img-caption">{t(`cat_${cat}`)}</span>
                {form.category === cat && <Check className="text-[var(--vl-gold)]" size={20} />}
              </div>
            </button>
          ))}
        </div>
      )}

      {step === 1 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-testid="step-datetime">
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-3">{t("choose_date")}</label>
            <div className="vl-card rounded-xl p-2 inline-block">
              <Calendar mode="single" selected={selectedDate} onSelect={onPickDate}
                disabled={[{ before: tomorrow() }, isClosedDay]} weekStartsOn={1} />
            </div>
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-3">{t("choose_time")}</label>
            {!form.date && <p className="text-sm text-[var(--vl-stone)]/70">—</p>}
            {form.date && slotsLoading && <Loader2 className="animate-spin text-[var(--vl-gold)]" size={18} />}
            {form.date && !slotsLoading && slots.length === 0 && (
              <p className="text-sm text-[var(--vl-stone)]" data-testid="no-slots">{t("no_slots")}</p>
            )}
            <div className="grid grid-cols-3 gap-2.5">
              {!slotsLoading && slots.map((s) => (
                <button key={s} onClick={() => set("time", s)}
                  className={`px-2 py-3 rounded-lg border text-sm transition-colors ${form.time === s ? "border-[var(--vl-gold)] text-[var(--vl-gold)] bg-[rgba(212,175,55,0.08)]" : "border-[var(--vl-border)] text-[var(--vl-ivory)] hover:border-[var(--vl-gold)]/50"}`}
                  data-testid={`booking-time-${s.replace(":", "")}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="step-details">
          <div className="sm:col-span-2">
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("full_name")}</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} className="vl-input rounded-lg px-4 py-3 w-full" data-testid="booking-name-input" />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("email")}</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="vl-input rounded-lg px-4 py-3 w-full" data-testid="booking-email-input" />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("phone")}</label>
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="vl-input rounded-lg px-4 py-3 w-full" data-testid="booking-phone-input" />
          </div>
          <div>
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("guests")}</label>
            <input type="number" min="0" max="10" value={form.guest_count} onChange={(e) => set("guest_count", parseInt(e.target.value) || 0)} className="vl-input rounded-lg px-4 py-3 w-full" data-testid="booking-guests-input" />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs tracking-widest uppercase text-[var(--vl-stone)] mb-2">{t("notes")}</label>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="vl-input rounded-lg px-4 py-3 w-full resize-none" data-testid="booking-notes-input" />
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3" data-testid="step-summary">
          {[
            [t("summary_category"), t(`cat_${form.category}`)],
            [t("summary_datetime"), `${form.date} · ${form.time}`],
            [t("summary_name"), form.name],
            [t("email"), form.email],
            [t("phone"), form.phone],
            [t("guests"), form.guest_count],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between border-b border-[var(--vl-border)] py-2.5">
              <span className="text-xs tracking-widest uppercase text-[var(--vl-stone)]">{k}</span>
              <span className="text-sm text-[var(--vl-ivory)] text-right">{v}</span>
            </div>
          ))}
          {form.notes && <p className="text-sm text-[var(--vl-stone)] pt-2">“{form.notes}”</p>}
        </div>
      )}

      <div className="flex items-center justify-between mt-9">
        <button onClick={() => setStep((s) => Math.max(presetCategory ? 1 : 0, s - 1))} disabled={step === (presetCategory ? 1 : 0)}
          className="vl-btn-outline rounded-full px-5 py-2.5 text-sm flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="wizard-back-btn">
          <ChevronLeft size={16} /> {t("back")}
        </button>
        {step < 3 ? (
          <button onClick={() => canNext() ? setStep((s) => s + 1) : toast.error(t("required_field"))}
            className="vl-btn-gold rounded-full px-7 py-2.5 text-sm font-medium flex items-center gap-1" data-testid="wizard-next-btn">
            {t("next")} <ChevronRight size={16} />
          </button>
        ) : (
          <button onClick={submit} disabled={loading} className="vl-btn-gold rounded-full px-7 py-2.5 text-sm font-medium flex items-center gap-2" data-testid="wizard-submit-btn">
            {loading && <Loader2 size={16} className="animate-spin" />} {t("confirm_booking")}
          </button>
        )}
      </div>
    </div>
  );
};

export default BookingWizard;
